import { useState, useEffect, useCallback } from "react";
import { CashAndBankRepositoryImpl } from "../../data/repositories/CashAndBankRepositoryImpl";
import { GetReconciliationStatusUseCase } from "../../domain/use-cases/GetReconciliationStatusUseCase";
import type { GetReconciliationStatusParams } from "../../domain/repositories/CashAndBankRepository";
import type { ReconciliationStatusItemEntity } from "../../domain/entities/ReconciliationStatusEntity";

const repository = new CashAndBankRepositoryImpl();
const getReconciliationStatusUseCase = new GetReconciliationStatusUseCase(
  repository
);

const DEFAULT_LIMIT = 5;

export interface UseReconciliationStatusResult {
  items: ReconciliationStatusItemEntity[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  pagination: {
    total: number;
    limit: number;
    page: number;
    totalPages: number;
    hasMore: boolean;
  };
  setPage: (page: number) => void;
}

export function useReconciliationStatus(): UseReconciliationStatusResult {
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<ReconciliationStatusItemEntity[]>([]);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: DEFAULT_LIMIT,
    page: 1,
    totalPages: 0,
    hasMore: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async (pageNum: number) => {
    setLoading(true);
    setError(null);
    const params: GetReconciliationStatusParams = {
      page: pageNum,
      limit: DEFAULT_LIMIT,
    };
    try {
      const entity = await getReconciliationStatusUseCase.execute(params);
      setItems(entity.items);
      setPagination({
        total: entity.pagination.total,
        limit: entity.pagination.limit,
        page: entity.pagination.page,
        totalPages: entity.pagination.totalPages,
        hasMore: entity.pagination.hasMore,
      });
      setPage(entity.pagination.page);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load reconciliation status"
      );
      setItems([]);
      setPagination({
        total: 0,
        limit: DEFAULT_LIMIT,
        page: 1,
        totalPages: 0,
        hasMore: false,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus(page);
  }, [page, fetchStatus]);

  const refetch = useCallback(() => fetchStatus(page), [fetchStatus, page]);

  return {
    items,
    loading,
    error,
    refetch,
    pagination,
    setPage,
  };
}
